import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Sync as SyncIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import {
  getShopifyProducts,
  deleteShopifyProduct,
  getShopifyProductCount,
} from '../services/shopifyAPIService';

const ShopifyProductsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [productCount, setProductCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchProductCount();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔄 Fetching Shopify products...');

      const response = await getShopifyProducts(250); // Fetch up to 250 products
      
      console.log('📦 Raw response:', response);

      let productsData = [];
      
      // ✅ Handle backend response structure: { IsSuccess, Data }
      if (response?.IsSuccess && response?.Data && Array.isArray(response.Data)) {
        productsData = response.Data;
      } 
      // ✅ Handle alternative structures
      else if (response?.payload && Array.isArray(response.payload)) {
        productsData = response.payload;
      } 
      else if (Array.isArray(response)) {
        productsData = response;
      }
      else if (response?.data && Array.isArray(response.data)) {
        productsData = response.data;
      }

      setProducts(productsData);
      console.log('✅ Shopify products loaded:', productsData.length);
      toast.success(`${productsData.length} products loaded from Shopify`);
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load Shopify products';
      setError(errorMessage);
      toast.error(errorMessage);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductCount = async () => {
    try {
      const response = await getShopifyProductCount();
      
      // ✅ Handle different response structures
      let count = 0;
      if (response?.IsSuccess && response?.Data?.count !== undefined) {
        count = response.Data.count;
      } else if (response?.payload?.count !== undefined) {
        count = response.payload.count;
      } else if (response?.count !== undefined) {
        count = response.count;
      }
      
      setProductCount(count);
    } catch (error) {
      console.error('❌ Error fetching product count:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    await fetchProducts();
    await fetchProductCount();
    setSyncing(false);
  };

  const handleDelete = async (productId, productTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${productTitle}" from Shopify?`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting Shopify product:', productId);
      await deleteShopifyProduct(productId);

      const updatedProducts = products.filter((p) => p.Id !== productId);
      setProducts(updatedProducts);

      // Reset page if current page would be empty
      const newProductCount = updatedProducts.length;
      const maxPage = Math.ceil(newProductCount / rowsPerPage) - 1;
      if (page > maxPage && maxPage >= 0) {
        setPage(maxPage);
      }

      toast.success('Product deleted successfully from Shopify');
      fetchProductCount();
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast.error('Failed to delete product from Shopify');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddProduct = () => {
    navigate('/shopify/products/add');
  };

  const handleEditProduct = (productId) => {
    navigate(`/shopify/products/edit/${productId}`);
  };

  const handleViewProduct = (productId) => {
    navigate(`/shopify/products/view/${productId}`);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'info';
    }
  };

  const filteredProducts = products.filter((product) => {
    // ✅ Updated to match backend property names (Pascal Case)
    const title = product.Title || '';
    const vendor = product.Vendor || '';
    const productType = product.ProductType || '';
    const tags = product.Tags || '';

    const matchesSearch =
      title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      productType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tags.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterType === 'all' ||
      (filterType === 'active' && product.Status?.toLowerCase() === 'active') ||
      (filterType === 'draft' && product.Status?.toLowerCase() === 'draft') ||
      (filterType === 'archived' && product.Status?.toLowerCase() === 'archived');

    return matchesSearch && matchesFilter;
  });

  const paginatedProducts = filteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ✅ Calculate stats using Pascal Case properties
  const activeProducts = products.filter((p) => p.Status?.toLowerCase() === 'active').length;
  const totalInventory = products.reduce((sum, p) => {
    const variants = p.Variants || [];
    return sum + variants.reduce((vSum, v) => vSum + (v.InventoryQuantity || 0), 0);
  }, 0);

  const totalValue = products.reduce((sum, p) => {
    const variants = p.Variants || [];
    return sum + variants.reduce((vSum, v) => vSum + ((v.Price || 0) * (v.InventoryQuantity || 0)), 0);
  }, 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}
          >
            🛍️ Shopify Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and sync your Shopify store products
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
          >
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddProduct}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 3 }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Products
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {productCount || products.length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56 }}>
                  <ShoppingCartIcon sx={{ fontSize: 30 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Active Products
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {activeProducts}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light', width: 56, height: 56 }}>
                  <TrendingUpIcon sx={{ fontSize: 30 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Inventory
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {totalInventory}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.light', width: 56, height: 56 }}>
                  <InventoryIcon sx={{ fontSize: 30 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Inventory Value
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    ${totalValue.toFixed(2)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.light', width: 56, height: 56 }}>
                  <AttachMoneyIcon sx={{ fontSize: 30 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search and Filter */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by title, vendor, type, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filterType}
                label="Filter"
                onChange={(e) => setFilterType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Products</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="draft">Draft Only</MenuItem>
                <MenuItem value="archived">Archived Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Chip
                label={`${filteredProducts.length} Products`}
                color="primary"
                variant="outlined"
                icon={<FilterListIcon />}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Products Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading Shopify products...
            </Typography>
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 8, textAlign: 'center' }}>
            <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || filterType !== 'all' ? 'No products found' : 'No Shopify products yet'}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              {searchTerm || filterType !== 'all' ? 'Try adjusting your search or filter criteria' : 'Start by adding your first product to Shopify'}
            </Typography>
            {!searchTerm && filterType === 'all' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProduct} sx={{ textTransform: 'none', borderRadius: 2 }}>
                Add First Product
              </Button>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Variants</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Inventory</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Price Range</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProducts.map((product) => {
                    // ✅ Updated to use Pascal Case properties
                    const variants = product.Variants || [];
                    const firstVariant = variants[0] || {};
                    const totalInventory = variants.reduce((sum, v) => sum + (v.InventoryQuantity || 0), 0);
                    const images = product.Images || [];
                    const firstImage = images[0];
                    const imageUrl = firstImage?.Src;

                    // Calculate price range
                    const prices = variants.map(v => v.Price || 0);
                    const minPrice = Math.min(...prices);
                    const maxPrice = Math.max(...prices);
                    const priceRange = minPrice === maxPrice 
                      ? `$${minPrice.toFixed(2)}` 
                      : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;

                    return (
                      <TableRow 
                        key={product.Id} 
                        hover 
                        sx={{ 
                          '&:hover': { backgroundColor: 'action.hover' }, 
                          borderBottom: '1px solid', 
                          borderColor: 'divider' 
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {imageUrl ? (
                              <Avatar src={imageUrl} variant="rounded" sx={{ width: 50, height: 50 }} />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 50, height: 50, bgcolor: 'grey.300' }}>
                                <InventoryIcon />
                              </Avatar>
                            )}
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {product.Title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {product.Id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{product.Vendor || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{product.ProductType || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${variants.length} variant${variants.length !== 1 ? 's' : ''}`} 
                            size="small" 
                            variant="outlined"
                            color="info"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={totalInventory} 
                            size="small" 
                            color={totalInventory > 0 ? 'success' : 'error'} 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {priceRange}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={product.Status || 'draft'} 
                            size="small" 
                            color={getStatusColor(product.Status)} 
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewProduct(product.Id)} sx={{ color: 'info.main' }}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditProduct(product.Id)} sx={{ color: 'warning.main' }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(product.Id, product.Title)} sx={{ color: 'error.main' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredProducts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
            />
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ShopifyProductsListPage;